// ====================================================================
// 3) src/components/SessionToast.jsx
//    Animated, accessible toast for session-expired notice
//    Uses Tailwind + Framer Motion + lucide-react (optional)
// ====================================================================
import React, { useEffect } from "react";
import { X, TimerReset } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

/**
 * Props: { notice: { type, message }, onClose: fn, autoHideMs?: number }
 * WHY: Show one-shot session message after redirect.
 */
export default function SessionToast({ notice, onClose, autoHideMs = 5000 }) {
  useEffect(() => {
    if (!notice) return;
    const id = setTimeout(onClose, autoHideMs);
    return () => clearTimeout(id);
  }, [notice, onClose, autoHideMs]);

  return (
    <AnimatePresence>
      {notice && (
        <motion.div
          initial={{ opacity: 0, y: -12, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -12, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          role="status"
          aria-live="polite"
          className="fixed top-4 right-4 z-[1000] max-w-sm"
        >
          <div className="rounded-2xl border bg-white/90 backdrop-blur shadow-lg px-4 py-3">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-red-500 text-white shadow">
                  <TimerReset className="h-4 w-4" />
                </span>
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold">
                  {notice.type === "session_expired"
                    ? "Session expired"
                    : "Notice"}
                </div>
                <div className="text-sm text-gray-700 mt-0.5">
                  {notice.message}
                </div>
              </div>
              <button
                aria-label="Dismiss notification"
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 p-1 rounded-lg focus:outline-none focus:ring"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 h-1 w-full overflow-hidden rounded-full bg-gray-200">
              <motion.div
                initial={{ width: "100%" }}
                animate={{ width: 0 }}
                transition={{ duration: autoHideMs / 1000, ease: "linear" }}
                className="h-full bg-gradient-to-r from-amber-500 to-red-500"
              />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
